import React, { FC } from "react";
import { createStyles, makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Checkbox,
  Grid,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
} from "@material-ui/core";
import { GuestInfo } from "../../model/guest-info";
import { SELF_GUIDED_TOUR } from "../constants";
import BookingService, { GuestBooking, AppointmentCancelRequest, STATUS_SCHEDULED } from "../../service/booking-service";
import moment from "moment-timezone";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Loader from "react-loader-spinner";
import d from "debug";

const debug = d("ConfirmAppointments");

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      margin: 0,
      padding: 0,
    },
    filterContainer: {
      width: "100%",
      minWidth: "1000px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
    },
    primaryButton: {
      color: "white",
      borderRadius: "0px",
      backgroundColor: "#EF4E22",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#AB3825",
        boxShadow: "none",
      },
      marginRight: theme.spacing(1),
    },
    table: {
      minWidth: 850,
      fontFamily: "Mallory",
      fontWeight: 600,
    },
    container: {
      maxHeight: 100,
    },
    screenCenter: {
      position: "fixed",
      top: "50%",
      left: "50%",
      width: "104px",
      height: "104px",
      marginTop: "-52px" /*set to a negative number 1/2 of your height*/,
      marginLeft: "-52px" /*set to a negative number 1/2 of your width*/,
      zIndex: 10000,
    },
  })
);

function validateEmail(email) {
  if (!email) {
    return false;
  }
  const regexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexp.test(email);
}
export interface ConfirmedAppointmentsProps {
  guestInfo: GuestInfo;
}

const ConfirmedAppointments: FC<ConfirmedAppointmentsProps> = ({ guestInfo }) => {
  const classes = useStyles(useTheme());
  const [guestBookings, setGuestBookings] = React.useState<GuestBooking[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [scheduledBookingsCount, setScheduledBookingsCount] = React.useState<number>(0);
  const [viewFilter, setViewFilter] = React.useState<string>(STATUS_SCHEDULED);
  const [email, setEmail] = React.useState<string>(guestInfo.emailAddress || "");
  const [validEmail, setValidEmail] = React.useState<boolean>(validateEmail(guestInfo.emailAddress));
  const [loading, setLoading] = React.useState<boolean>(false);
  const [errorResponseText, setErrorResponseText] = React.useState<string>("");

  const refreshGuestAppointments = () => {
    if (email && validateEmail(email)) {
      setLoading(true);
      const bookingService = new BookingService();
      bookingService
        .guestBookings(email)
        .then((response) => {
          // console.log("bookingService.guestBookingArray: ", response.data);
          const filteredData = response.data
            .map((booking) => {
              return {
                status: booking.Status__c,
                confirmationNumber: booking.Name || "",
                guestCardId: booking.Contact__c,
                propertyId: booking.Account__c,
                propertyName: booking.Account__r?.Name || "",
                tourType: booking.Tour_Type__c,
                startTime: moment(booking.Tour_Start_Time__c),
                endTime: booking.Tour_End_Time__c ? moment(booking.Tour_End_Time__c) : moment(booking.Tour_Start_Time__c).add(1, "hour"),
              };
            })
            .filter((b) => b.status === viewFilter || viewFilter === "All")
            .sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());
          // console.log("filtered Booking Data: ", filteredData);
          setGuestBookings(filteredData);
          setScheduledBookingsCount(getScheduledBookingsCount());
          setSelected([]);
        })
        .catch((error) => {
          setGuestBookings([]);
          setSelected([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setGuestBookings([]);
      setSelected([]);
    }
  };

  React.useEffect(() => {
    refreshGuestAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter]);

  React.useEffect(() => {
    refreshGuestAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestInfo]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      let newSelecteds: string[] = [];
      if (guestBookings) {
        newSelecteds = guestBookings.filter((item) => item.status === STATUS_SCHEDULED).map((n) => n.confirmationNumber);
      }
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value as string);
    setValidEmail(validateEmail(event.target.value as string));
  };

  const handleClick = (event: React.MouseEvent<unknown>, booking: GuestBooking) => {
    if (booking && booking.status === STATUS_SCHEDULED) {
      const selectedIndex = selected.indexOf(booking.confirmationNumber);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, booking.confirmationNumber);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
      }
      setSelected(newSelected);
    }
  };

  const handleCancel = () => {
    debug("handleCancel");
    setLoading(true);
    setErrorResponseText("");
    const bookingService = new BookingService();
    const cancelRequest: AppointmentCancelRequest = {
      appointmentsList: [],
    };
    cancelRequest.appointmentsList = selected.map((id) => {
      return { appointmentNumber: id, cancelReason: "unknown" };
    });
    bookingService
      .cancelAppointments(cancelRequest)
      .then((response) => {
        debug("cancel response: ", response);
        const resp: any = response;
        if (resp && resp.data && resp.data[0] && resp.data[0].errorCode) {
          setErrorResponseText(resp.data[0].errorCode + ": " + resp.data[0].message);
        }
      })
      .finally(() => {
        setLoading(false);
        refreshGuestAppointments();
      });
  };

  const handleFilterClick = (event: any) => {
    setViewFilter(event.target.value as string);
  };

  const isBookingSelected = (confNumber: string): boolean => {
    return selected.indexOf(confNumber) >= 0;
  };

  const getScheduledBookingsCount = (): number => {
    let count = 0;
    if (guestBookings) {
      count = guestBookings.filter((item) => item.status === STATUS_SCHEDULED).length;
    }
    return count;
  };

  return (
    <div className={classes.root}>
      <Grid container direction="row" justify="space-between" alignItems="center" spacing={2}>
        <Grid item xs={6} container direction="row" justify="flex-start" alignItems="center" spacing={1}>
          <Grid item xs={5}>
            <TextField
              fullWidth
              size="small"
              style={{ marginBottom: "8px" }}
              variant="outlined"
              label="Email Address*"
              value={email}
              onChange={handleEmailChange}
            />
          </Grid>
          <Grid item xs={4}>
            <Button
              fullWidth
              size="large"
              className={classes.primaryButton}
              style={{ marginBottom: "8px", minWidth: "225px" }}
              variant="contained"
              color="secondary"
              disabled={loading || !validEmail}
              onClick={refreshGuestAppointments}
            >
              <Typography variant="button" noWrap>
                Fetch Appointments
              </Typography>
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <RadioGroup value={viewFilter} className={classes.filterContainer} onChange={handleFilterClick}>
            <FormControlLabel key="Scheduled" value="Scheduled" control={<Radio size="small" />} label="Scheduled" />
            <FormControlLabel key="All" value="All" control={<Radio size="small" />} label="All" />
          </RadioGroup>
        </Grid>
      </Grid>
      {errorResponseText && (
        <Grid item xs={12}>
          <Typography variant="caption" color="error">
            {errorResponseText}
          </Typography>
        </Grid>
      )}
      <TableContainer>
        <Table className={classes.table} aria-label="appointments table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && guestBookings && selected.length < scheduledBookingsCount}
                  checked={guestBookings && guestBookings.length > 0 && selected.length === scheduledBookingsCount}
                  onChange={handleSelectAllClick}
                  inputProps={{ "aria-label": "select all bookings" }}
                />
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tour Type</TableCell>
              <TableCell>Neighborhood</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Confirmation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {guestBookings &&
              guestBookings.map((booking, index) => {
                const isItemSelected = isBookingSelected(booking.confirmationNumber);
                const labelId = `booking-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, booking)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={booking.confirmationNumber}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        disabled={booking.status !== STATUS_SCHEDULED}
                        checked={isItemSelected}
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                    </TableCell>
                    <TableCell>{booking.status}</TableCell>
                    <TableCell>{booking.tourType}</TableCell>
                    <TableCell component="th" scope="row">
                      {booking.propertyName}
                    </TableCell>
                    <TableCell>{booking.startTime.format("dddd, MMMM Do")}</TableCell>
                    <TableCell>{(moment.tz(booking.startTime,'America/Los_Angeles')).format(("h:mm A z"))}</TableCell>
                    <TableCell>{booking.confirmationNumber}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <br />
      <Grid container spacing={10}>
        <Grid item>
          <Button
            className={classes.primaryButton}
            variant="contained"
            color="secondary"
            disabled={loading || !selected || selected.length === 0}
            onClick={handleCancel}
          >
            Cancel Selected
          </Button>
        </Grid>
      </Grid>
      <br />
      <div className={classes.screenCenter}>
        <Loader
          visible={loading}
          type="BallTriangle"
          color="#EF4E22"
          height={100}
          width={100}
          timeout={3000} //3 secs
        />
      </div>
    </div>
  );
};

export default ConfirmedAppointments;
